import { graphql } from 'react-relay';
import { useMemo } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { groupMeetings } from './groupMeetings.ts';
import { mapLoomMeeting } from './mapLoomMeeting.ts';
import type {
	Meeting,
	MeetingDateRange,
	MeetingsEligibility,
	UseMeetingsResult,
} from './meetingTypes.ts';
import type {
	useMeetingsQuery as useMeetingsQuery$data,
	useMeetingsQueryVariables,
} from './__types__/useMeetingsQuery.ts';

/**
 * Live Relay-backed implementation of the Meetings data hook.
 *
 * This is the single seam between the UI and the data layer — every consuming
 * component depends only on the `UseMeetingsResult` contract, never on the query.
 *
 * Data sources (see tech spec https://hello.atlassian.net/wiki/spaces/Growth/pages/7345509583):
 * - `loom_meetingsSearch(id: siteAri)` → the user's recorded (notetaker) meetings.
 *   Returns `{ single, recurring }` with NO date-range argument (30-day server
 *   default), so we client-filter to the requested range (default: last 7 days).
 *   NOTE: swap to a range-capable query when the Loom team ships one.
 * - `loom_settings(siteId)` → drives eligibility (TWC / connect-calendar / show).
 *
 * Out of scope here: action items (recap + one-click actions) — rendered by the
 * swappable `ActionItem` boundary and left empty until the one-click-actions AGG
 * query is available.
 */

const MEETINGS_QUERY = graphql`
	query useMeetingsQuery($siteAri: ID!, $cloudId: ID!) {
		loom_settings(siteId: $cloudId) {
			meetingNotesAvailable
			meetingNotesEnabled
		}
		loom_meetingsSearch(id: $siteAri) {
			single {
				id
				title
				startsAt
				recorderId
				recorder {
					accountId
					name
					picture
				}
				inviteeAris
				invitees {
					accountId
					name
					picture
				}
				video {
					id
					url
					isMeeting
					meetingAri
				}
			}
			recurring {
				id
				title
				startsAt
				recorderId
				recorder {
					accountId
					name
					picture
				}
				inviteeAris
				invitees {
					accountId
					name
					picture
				}
				video {
					id
					url
					isMeeting
					meetingAri
				}
			}
		}
	}
`;

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

export type UseMeetingsOptions = {
	/** Date range to fetch meetings for. Defaults to the last 7 days. */
	dateRange?: MeetingDateRange;
};

/** Build the Loom site ARI used by `loom_meetingsSearch`. */
const toSiteAri = (cloudId: string): string => `ari:cloud:loom::site/${cloudId}`;

/**
 * Derive the eligibility state from Loom settings.
 * - settings missing / not available → user isn't on TWC → cross-flow banner.
 * - available but not enabled → treat as "connect calendar" onboarding.
 * - available + enabled → eligible.
 */
const deriveEligibility = (
	settings: useMeetingsQuery$data['loom_settings'] | undefined,
): Extract<MeetingsEligibility, 'eligible' | 'noTwc' | 'noConnectedCalendar'> => {
	if (!settings?.meetingNotesAvailable) {
		return 'noTwc';
	}
	if (!settings.meetingNotesEnabled) {
		return 'noConnectedCalendar';
	}
	return 'eligible';
};

export const useMeetings = (options: UseMeetingsOptions = {}): UseMeetingsResult => {
	const { dateRange } = options;
	const { cloudId, userId } = useSessionData();

	const rangeStart = dateRange?.rangeStart;
	const rangeEnd = dateRange?.rangeEnd;

	const { data, loading, error } = useQueryInterop<
		useMeetingsQuery$data,
		useMeetingsQueryVariables
	>(MEETINGS_QUERY, {
		variables: { siteAri: toSiteAri(cloudId), cloudId },
		errorPolicy: 'all',
		fetchPolicy: 'cache-first',
	});

	const eligibility: MeetingsEligibility = loading
		? 'loading'
		: deriveEligibility(data?.loom_settings);

	const groups = useMemo(() => {
		if (loading || eligibility !== 'eligible' || !userId) {
			return [];
		}

		const search = data?.loom_meetingsSearch;
		const rawMeetings = [...(search?.single ?? []), ...(search?.recurring ?? [])];

		// Interim date window: use the caller's range, else the last 7 days.
		const lowerBound = rangeStart ? Date.parse(rangeStart) : Date.now() - SEVEN_DAYS_MS;
		const upperBound = rangeEnd ? Date.parse(rangeEnd) : Date.now();

		// Single pass: keep only meetings the user recorded or was invited to
		// (attended), that have a playable recording, and that fall in range.
		const meetings: Meeting[] = [];
		for (const m of rawMeetings) {
			if (!m) {
				continue;
			}
			const isParticipant = m.recorderId === userId || (m.inviteeAris ?? []).includes(userId);
			if (!isParticipant || !m.video?.url) {
				continue;
			}
			const mapped = mapLoomMeeting(m);
			const t = Date.parse(mapped.startsAt);
			if (Number.isNaN(t) || t < lowerBound || t > upperBound) {
				continue;
			}
			meetings.push(mapped);
		}
		meetings.sort((a, b) => Date.parse(b.startsAt) - Date.parse(a.startsAt));

		return groupMeetings(meetings);
	}, [loading, eligibility, userId, data, rangeStart, rangeEnd]);

	const totalCount = useMemo(
		() => groups.reduce((sum, group) => sum + group.meetings.length, 0),
		[groups],
	);

	return {
		eligibility,
		loading,
		error: error ?? undefined,
		groups,
		totalCount,
	};
};
